import React, { useEffect, useRef, useMemo } from "react";
import * as echarts from "echarts";
import FormCard from "../Components/Forms/FormCard";

const InferenceTimeSeriesChart = ({
  inferenceData = {},
  selectedAnomaly,
  forecastData = {},
}) => {
  const chartRef = useRef(null);

  // Prepare chart data from object format only
  const chartData = useMemo(() => {
    if (
      !inferenceData ||
      !inferenceData.tag_aliases ||
      !inferenceData.timestamps ||
      !inferenceData.values ||
      !inferenceData.anomalies
    ) {
      return {
        keys: [],
        values: [],
        timestamp: [],
        anomalies: [],
        alarmAnomalies: [],
      };
    }
    const keys = inferenceData.tag_aliases;
    const timestamp = inferenceData.timestamps;
    const alarmAnomalySource =
      inferenceData.alarm_anomalies || inferenceData.alaram_anomalies || {};
    // values: for each timestamp, array of values for each key
    const values = timestamp.map((_, tIdx) =>
      keys.map((key) => inferenceData.values[key][tIdx])
    );
    // anomalies: for each timestamp, array of anomaly values for each key
    const anomalies = timestamp.map((_, tIdx) =>
      keys.map((key) =>
        inferenceData.anomalies[key]?.[tIdx] === 1 ||
        inferenceData.anomalies[key]?.[tIdx] === "1"
          ? inferenceData.values[key]?.[tIdx]
          : null
      )
    );
    const alarmAnomalies = timestamp.map((_, tIdx) =>
      keys.map((key) => {
        const alarmValue = alarmAnomalySource[key]?.[tIdx];
        if (alarmValue === null || alarmValue === undefined || alarmValue === 0 || alarmValue === "0") {
          return null;
        }
        if (alarmValue === 1 || alarmValue === "1") {
          return inferenceData.values[key]?.[tIdx];
        }
        return alarmValue;
      })
    );
    return { keys, values, timestamp, anomalies, alarmAnomalies };
  }, [inferenceData]);

  // Prepare future data
  const futurePredictions = useMemo(() => {
    if (
      !forecastData ||
      !forecastData.forecast ||
      !forecastData.forecast.forecast_timestamps ||
      !forecastData.forecast.forecast_values ||
      forecastData.forecast.forecast_timestamps.length === 0
    ) {
      return { hasforecastData: false, forecast: null };
    }

    return {
      hasforecastData: true,
      forecast: forecastData.forecast,
    };
  }, [forecastData]);

  const createNewChart = () => {
    const anomalyLegendData = chartData.keys.map((key) => `Ano-${key}`);
    const alarmAnomalyLegendData = chartData.keys.map(
      (key) => `Alarm-Ano-${key}`
    );
    const futureLegendData = futurePredictions.hasforecastData
      ? [`Future-${futurePredictions.forecast.tag}`]
      : [];

    const legendData = [
      ...chartData.keys,
      ...futureLegendData,
      ...anomalyLegendData,
      ...alarmAnomalyLegendData,
    ];
    const legendSelected = legendData.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});

    // Combine regular timestamps with future timestamps
    const allTimestamps = futurePredictions.hasforecastData
      ? [
          ...chartData.timestamp,
          ...futurePredictions.forecast.forecast_timestamps,
        ]
      : chartData.timestamp;

    const chartOption = {
      title: {
        text: "Inference Data",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
        },
      },
      grid: {
        left: "11%",
        right: "11%",
        top: 80,
        containLabel: true,
      },
      toolbox: {
        feature: {
          dataView: { show: true, readOnly: false },
          restore: { show: true },
          dataZoom: {
            yAxisIndex: "none",
          },
          saveAsImage: {},
        },
      },
      legend: {
        data: legendData,
        selected: legendSelected,
        orient: "horizontal",
        align: "auto",
        top: 35,
        itemGap: 12,
        type: "scroll",
        formatter: function (name) {
          return name;
        },
        selectedMode: "multiple",
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: allTimestamps || [],
      },
      yAxis: [],
      dataZoom: [
        {
          type: "slider",
        },
        {
          type: "inside",
        },
      ],
      series: [],
    };

    const colorPalette = [
      "#5470C6", // blue
      "#8cdf65", // green
      "#0c802f", // yellow
      "#73C0DE", // cyan
      "#9A60B4", // purple
      "#EA7CCC", // pink
    ];

    chartData.keys.forEach((key, index) => {
      const seriesData = chartData.values.map((value) => value[index]);
      const anomalyData = chartData.anomalies.map((anomaly) => anomaly[index]);
      const alarmAnomalyData = chartData.alarmAnomalies.map(
        (anomaly) => anomaly[index]
      );
      // Get units
      const unit =
        inferenceData.tag_units && inferenceData.tag_units[index]
          ? ` (${inferenceData.tag_units[index]})`
          : "";

      // Main series with explicit color
      chartOption.series.push({
        name: key,
        type: "line",
        yAxisIndex: index,
        symbol: "none",
        sampling: "lttb",
        data: seriesData,
        legendHoverLink: true,
        lineStyle: { color: colorPalette[index % colorPalette.length] },
        itemStyle: { color: colorPalette[index % colorPalette.length] },
      });

      // Anomaly series, bright red
      chartOption.series.push({
        name: `Ano-${key}`,
        type: "line",
        yAxisIndex: index,
        symbol: "circle",
        sampling: "lttb",
        data: anomalyData,
        lineStyle: {
          type: "dotted",
          color: "#E53935",
        },
        itemStyle: {
          color: "#E53935",
        },
      });

      chartOption.series.push({
        name: `Alarm-Ano-${key}`,
        type: "line",
        yAxisIndex: index,
        symbol: "circle",
        sampling: "lttb",
        data: alarmAnomalyData,
        lineStyle: {
          type: "dotted",
          color: "#FAC858",
        },
        itemStyle: {
          color: "#FAC858",
        },
      });

      // Add future prediction series if available
      if (
        futurePredictions.hasforecastData &&
        futurePredictions.forecast.tag === key
      ) {
        const forecast = futurePredictions.forecast;
        if (forecast.forecast_values && forecast.forecast_values.length > 0) {
          // Pad forecast data with nulls for historical timestamps
          const paddedForecastData = [
            ...new Array(chartData.timestamp.length).fill(null),
            ...forecast.forecast_values,
          ];

          chartOption.series.push({
            name: `Future-${key}`,
            type: "line",
            yAxisIndex: index,
            symbol: "diamond",
            symbolSize: 8,
            sampling: "lttb",
            data: paddedForecastData,
            legendHoverLink: true,
            smooth: true,
             lineStyle: {
              type: "dashed",
              color: colorPalette[index % colorPalette.length],
            },
            itemStyle: {
              color: colorPalette[index % colorPalette.length],
              borderColor: "#fff",
              borderWidth: 3,
              opacity: 0.9,
            },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: colorPalette[index % colorPalette.length] + "40",
                  },
                  {
                    offset: 1,
                    color: colorPalette[index % colorPalette.length] + "10",
                  },
                ],
              },
            },
            emphasis: {
              focus: "series",
            },
          });
        }
      }

      chartOption.yAxis.push({
        type: "value",
        name: `${key}${unit}`,
        position: index % 2 === 0 ? "left" : "right",
        offset: Math.floor(index / 2) * 60,
        alignTicks: true,
        nameLocation: "middle",
        nameGap: 32,
        nameRotate: 90,
        axisLine: {
          show: true,
        },
        axisLabel: {
          formatter: "{value}",
          rotate: 45,
        },
        containLabel: true,
      });
    });

    return chartOption;
  };

  useEffect(() => {
    const chartInstance = echarts.init(chartRef.current);
    const chartOption = createNewChart();
    chartInstance.setOption(chartOption);

    const handleResize = () => {
      chartInstance.resize();
    };
    window.addEventListener("resize", handleResize);

    // Highlight and zoom to selected anomaly if present
    if (
      selectedAnomaly &&
      selectedAnomaly.DEP_TAG_ALIAS &&
      selectedAnomaly.TIMESTAMP
    ) {
      const tagIndex = chartData.keys.indexOf(selectedAnomaly.DEP_TAG_ALIAS);
      const timeIndex = chartData.timestamp.indexOf(selectedAnomaly.TIMESTAMP);
      if (tagIndex !== -1 && timeIndex !== -1) {
        const selectedSeriesIndex = chartOption.series.findIndex(
          (series) => series.name === selectedAnomaly.DEP_TAG_ALIAS
        );
        chartInstance.dispatchAction({
          type: "highlight",
          seriesIndex: selectedSeriesIndex,
          dataIndex: timeIndex,
        });
        const windowSize = 10;
        let start = Math.max(0, timeIndex - Math.floor(windowSize / 2));
        let end = Math.min(
          chartData.timestamp.length - 1,
          start + windowSize - 1
        );
        start = Math.max(0, end - windowSize + 1);
        chartInstance.dispatchAction({
          type: "dataZoom",
          startValue: chartData.timestamp[start],
          endValue: chartData.timestamp[end],
        });
        setTimeout(() => {
          chartInstance.dispatchAction({
            type: "showTip",
            seriesIndex: selectedSeriesIndex,
            dataIndex: timeIndex,
          });
        }, 200);
      }
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      chartInstance.dispose();
    };
  }, [chartData, selectedAnomaly, futurePredictions]);

  return (
    <FormCard>
      <div
        ref={chartRef}
        style={{ height: "100%", minHeight: "400px", width: "100%" }}
      />
    </FormCard>
  );
};

export default InferenceTimeSeriesChart;
